// src/Components/ConfirmDialog/ConfirmDialog.js

import React, { useEffect } from 'react';
import Dialog from '@mui/material/Dialog';
import IconButton from '@mui/material/IconButton';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';

// Icons for the Circular Buttons
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';

export default function ConfirmDialog({
  open,
  onClose,
  onConfirm,
  message,
  severity = 'error',
  icon,
  autoClose = 0,
  sx,
  confirmText,
  cancelText,
  hideCancel = false,
  disableBackdropClick = false, // AMENDED: New prop to restrict closing
  ...props
}) {
  // 1. Auto-close Logic
  useEffect(() => {
    if (open && autoClose > 0) {
      const timer = setTimeout(() => {
        onClose();
      }, autoClose);
      return () => clearTimeout(timer);
    }
  }, [open, autoClose, onClose]);

  // AMENDED: Intercept the close request to ignore backdrop clicks and escape key
  const handleDialogClose = (event, reason) => {
    if (
      disableBackdropClick &&
      (reason === 'backdropClick' || reason === 'escapeKeyDown')
    ) {
      return; // Do nothing, force the dialog to remain open
    }
    if (onClose) onClose(event, reason);
  };

  const showActions = !autoClose && onConfirm;

  return (
    <Dialog
      open={open}
      onClose={handleDialogClose} // AMENDED: Use the new interceptor function
      disableRestoreFocus
      {...props}
      sx={{
        // 2. CONSISTENT POSITIONING (Top Center)
        '& .MuiDialog-container': {
          alignItems: 'flex-start',
          paddingTop: '96px',
        },
        ...(sx || {}),
      }}
      slotProps={{
        backdrop: {
          sx: { backgroundColor: 'rgba(0, 0, 0, 0.1)' },
        },
        paper: {
          sx: {
            backgroundColor: 'transparent',
            boxShadow: 'none',
            maxWidth: 'none',
          },
        },
      }}>
      <Alert
        severity={severity}
        icon={icon}
        sx={{
          minWidth: '350px',
          alignItems: 'center',
          fontWeight: 500,
          border: '1px solid rgba(0,0,0,0.1)',
          boxShadow: '0px 4px 10px rgba(0,0,0,0.1)',
        }}
        action={
          showActions && (
            <Box sx={{ display: 'flex', gap: 1 }}>
              {/* 3. CANCEL BUTTON (Circular Cross) */}
              {!hideCancel && (
                <IconButton
                  size='small'
                  onClick={onClose}
                  sx={{
                    color: '#d32f2f', // Standard Red for Cancel
                    '&:hover': { backgroundColor: 'rgba(211, 47, 47, 0.1)' },
                  }}
                  title={cancelText || 'Cancel'}>
                  <CancelIcon fontSize='large' />
                </IconButton>
              )}

              {/* 4. CONFIRM BUTTON (Circular Tick) */}
              <IconButton
                size='small'
                onClick={onConfirm}
                sx={{
                  color: '#2e7d32', // Standard Green for OK
                  '&:hover': { backgroundColor: 'rgba(46, 125, 50, 0.1)' },
                }}
                title={confirmText || 'Confirm'}>
                <CheckCircleIcon fontSize='large' />
              </IconButton>
            </Box>
          )
        }>
        {message || 'Alert'}
      </Alert>
    </Dialog>
  );
}
